import { useCallback, useMemo } from 'react';
import { createColumnFilterRenderer } from '../../components/tables/createColumnFilterRenderer';
import { Alarm, useGetMachinesQuery } from '../../gql/generated';
import { useAlarmFilters } from '../../hooks/useAlarmFilters';
import { toOptions, uniqueOptions } from '../../utils/options';
import { AlarmTableRow } from './alarmRows';

/**
 * Hook that returns a columnFilters render function for the DataTable.
 * Each column gets its corresponding filter component.
 * Reads/writes filter state directly from URL query params - no props needed.
 *
 * The signal options derive from the alarms the table already fetched: that
 * list is unfiltered (only scoped to the user toggle), so every signal stays
 * offered while a filter is active.
 */
export function useAlarmColumnFilters(alarms: Alarm[], alarmsLoading: boolean) {
  const { filters, setName, setMachines, setSignals } = useAlarmFilters();
  // Fetch machines for filter
  const { data: machinesData, loading: machinesLoading } = useGetMachinesQuery({
    variables: {
      pagination: {
        limit: 1000,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // Transform machine options
  const machineOptions = useMemo(
    () =>
      toOptions(
        machinesData?.machines.rows,
        (machine) => machine.machineId,
        (machine) => machine.displayName || machine.machineId,
        true
      ),
    [machinesData]
  );

  // Extract unique signals
  const signalOptions = useMemo(
    () => uniqueOptions(alarms.map((alarm) => alarm.signal)),
    [alarms]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
    },
    [setName]
  );

  const handleMachinesChange = useCallback(
    (values: string[]) => {
      setMachines(values);
    },
    [setMachines]
  );

  const handleSignalsChange = useCallback(
    (values: string[]) => {
      setSignals(values);
    },
    [setSignals]
  );

  return createColumnFilterRenderer<AlarmTableRow>([
    {
      columnId: 'alarmName',
      kind: 'search',
      value: filters.name,
      onChange: handleNameChange,
      placeholder: 'Search Pulse alerts...',
    },
    {
      columnId: 'machine',
      kind: 'select',
      selectedValues: filters.machines,
      onChange: handleMachinesChange,
      options: machineOptions,
      placeholder: 'Search machines...',
      loading: machinesLoading,
    },
    {
      columnId: 'signal',
      kind: 'select',
      selectedValues: filters.signals,
      onChange: handleSignalsChange,
      options: signalOptions,
      placeholder: 'Search signals...',
      loading: alarmsLoading,
    },
  ]);
}
